/**
 * components/wishlist/vibe-card.tsx
 *
 * 2×3 grid card for the /wishlist home screen.
 * Shows vibe icon, name, item count, and last-added preview line.
 */
import { useNavigate } from 'react-router-dom'
import type { WishlistVibe, WishlistItem } from '@just-us/shared'
import { VIBE_META } from './vibe-chip'

interface VibeCardProps {
  vibe: WishlistVibe
  items: WishlistItem[]
  partnerNickname: string
}

export default function VibeCard({ vibe, items, partnerNickname }: VibeCardProps) {
  const navigate = useNavigate()
  const meta = VIBE_META[vibe]
  const pendingCount = items.filter((i) => !i.doneAt).length
  const lastItem = items.find((i) => !i.doneAt) ?? items[0] ?? null

  return (
    <button
      type="button"
      onClick={() => navigate(`/wishlist/${vibe}`)}
      className="
        flex flex-col items-start p-4 rounded-[20px] border
        active:scale-[0.97] transition-all duration-150
        touch-manipulation text-left w-full
      "
      style={{
        background: meta.bg,
        borderColor: meta.border,
      }}
    >
      {/* Icon + name */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[26px] leading-none">{meta.icon}</span>
        <span
          className="font-['DM_Sans'] text-[14px] font-semibold"
          style={{ color: meta.text }}
        >
          {meta.label}
        </span>
      </div>

      {/* Item count badge */}
      <div
        className="inline-flex items-center gap-1 px-[8px] py-[2px] rounded-full border text-[11px] font-['DM_Sans'] font-medium mb-2"
        style={{ borderColor: meta.border, color: meta.text, background: 'rgba(255,255,255,0.5)' }}
      >
        {pendingCount === 0 ? 'all done ✓' : `${pendingCount} ${pendingCount === 1 ? 'item' : 'items'}`}
      </div>

      {/* Last added preview */}
      {lastItem && (
        <p
          className="font-['DM_Sans'] text-[12px] truncate w-full"
          style={{ color: meta.text, opacity: 0.75 }}
        >
          {lastItem.isAddedByMe
            ? `You · ${lastItem.title}`
            : `${partnerNickname} · ${lastItem.title}`}
        </p>
      )}

      {!lastItem && (
        <p
          className="font-['DM_Sans'] text-[12px] italic"
          style={{ color: meta.text, opacity: 0.5 }}
        >
          Nothing yet — add something!
        </p>
      )}
    </button>
  )
}
